import { addressT } from '@ordermentum/ui-types';
import GoogleMapsLoader from './google_maps_loader';

type GoogleAutocomplete = {
  unbindAll: () => void;
  setComponentRestrictions: (data: object) => void;
  setFields: (fields: string[]) => void;
  addListener: (type: string, callback: () => void) => void;
  getPlace: () => {
    // eslint-disable-next-line camelcase
    address_components: any;
  };
  set: (tag: string, value: any) => void;
} | null;

export type CompleteAddressT = addressT & { placeId?: string; name?: string };

let placesApiObject: GoogleAutocomplete;
let map;

export const loadGoogleMaps = async () => {
  try {
    map = await GoogleMapsLoader(process.env.GOOGLE_MAPS_API_KEY);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error loading google maps');
  }
};

export const getLocationResults = async (search: string) => {
  const results =
    await new google.maps.places.AutocompleteService().getPlacePredictions({
      input: search,
    });
  return results.predictions;
};

export const getPlaceDetails = async (placeId: string) => {
  const result: google.maps.places.PlaceResult = await new Promise(
    (resolve, reject) => {
      const service = new google.maps.places.PlacesService(
        document.createElement('div')
      );
      service.getDetails(
        {
          placeId,
        },
        place => (place ? resolve(place) : reject())
      );
    }
  );

  return result;
};

export const parseAddress = (
  placeObject: google.maps.places.PlaceResult
): addressT => {
  if (!placeObject || !placeObject.address_components) {
    return {};
  }
  const addressObject: CompleteAddressT = {
    placeId: placeObject.place_id,
    street1: '',
    formatted: placeObject?.formatted_address ?? '',
  };
  let newAddress = '';
  for (const component of placeObject.address_components) {
    const [addressType] = component.types;
    switch (addressType) {
      case 'subpremise':
        newAddress = `${component.short_name}/${newAddress}`;
        break;
      case 'street_number':
        newAddress = `${newAddress + component.short_name} `;
        break;
      case 'route':
        newAddress += component.long_name;
        break;
      case 'locality':
        addressObject.suburb = component.long_name;
        break;
      case 'administrative_area_level_1':
        addressObject.state = component.short_name;
        break;
      case 'postal_code':
        addressObject.postcode = component.short_name;
        break;
      case 'country':
        addressObject.country = component.long_name;
        break;
      default:
        break;
    }
  }
  if (placeObject.name) {
    addressObject.name = placeObject.name;
  }
  addressObject.street1 = newAddress;
  return addressObject;
};

export const parseGooglePlacesObject = places => {
  const place = places?.getPlace();
  if (!place || !place.address_components) {
    return {};
  }

  return parseAddress(place);
};

export const initialiseMaps = async (inputRef, fillInAddress) => {
  if (!map) {
    return null;
  }

  try {
    placesApiObject = new map.places.Autocomplete(inputRef.current);
    placesApiObject?.setFields(['address_component']);
    placesApiObject?.setComponentRestrictions({ country: 'au' });
    placesApiObject?.addListener('place_changed', fillInAddress);

    return placesApiObject;
  } catch (err) {
    return null;
  }
};

export const isCompleteAddress = (addressObject: CompleteAddressT) =>
  addressObject.street1 &&
  addressObject.suburb &&
  addressObject.state &&
  addressObject.postcode &&
  addressObject.country;

export const unbindGoogleMaps = () => {
  if (placesApiObject) {
    placesApiObject.unbindAll();
  }
};

export const destroyGoogleMaps = () => {
  if (placesApiObject) {
    placesApiObject.unbindAll();
    placesApiObject = null;
  }
};
